import detectMobileDevice from 'is-mobile';
import PropTypes from 'prop-types';
import React from 'react';
import { Rnd } from 'react-rnd';
import styled, { css, keyframes } from 'styled-components';

import { useStores } from '../../stores';
import { detectBrowser } from '../../utilities';
import Fab from '../Fab';
import SpeechBubble from './SpeechBubble';

export default function VaIcon({
  isOpened,
  isMinimized,
  onClick,
  position,
  size,
  img,
  icon,
  textFontFamily,
  isAnimation,
  isHidden,
  isSpeechBubble,
  speechBubbleText,
  roundedBorder,
  ...props
}) {
  const isMobile = detectMobileDevice();
  const { isAndroid } = detectBrowser;
  const { ui } = useStores();
  const [positionState, setPositionState] = React.useState({
    x: position.x,
    y: position.y
  });
  const [windowSize, setWindowSize] = React.useState({ width: 0, height: 0 });
  const [showSpeechBubble, setShowSpeechBubble] = React.useState(false);
  let drag = false;
  const dragStartPosition = { x: 0, y: 0 };

  const handleClick = (e, ...args) => {
    if (onClick) {
      onClick(e, ...args);
    }
  };

  React.useEffect(() => {
    if (ui.windowSize.width !== windowSize.width || ui.windowSize.height !== windowSize.height) {
      setPositionState({ x: position.x, y: position.y });
    }
  }, [
    position.x,
    position.y,
    ui.windowSize.height,
    ui.windowSize.width,
    windowSize.height,
    windowSize.width
  ]);

  React.useEffect(() => {
    const updateWindowDimensions = () => {
      const newSize = ui.windowSize;
      setWindowSize(newSize);
    };

    window.addEventListener('resize', updateWindowDimensions);

    return () => window.removeEventListener('resize', updateWindowDimensions);
  }, [ui.windowSize]);

  React.useEffect(() => {
    if (isSpeechBubble) {
      setShowSpeechBubble(true);
    }
  }, [isSpeechBubble]);

  const isHiddenButton = (isOpened && !isMinimized) || (isHidden && !isMinimized);
  const [isHover, setIsHover] = React.useState(false);

  const handleMouseEnter = () => {
    if (img.urlHover) setIsHover(true);
  };

  const handleMouseLeave = () => {
    if (img.urlHover) setIsHover(false);
  };

  return (
    <Wrapper>
      <Rnd
        cancel=".react-rnd-draggable-cancel"
        style={{
          visibility: isHiddenButton ? 'hidden' : 'visible',
          position: 'fixed',
          zIndex: 9999999,
          cursor: 'pointer'
        }}
        bounds="window"
        size={{
          width: size.width,
          height: size.height
        }}
        minWidth={size.width}
        minHeight={size.height}
        position={{
          x: positionState.x,
          y: positionState.y
        }}
        onDragStart={e => {
          drag = false;
          if (isMobile && isAndroid) {
            dragStartPosition.x = e.changedTouches[0].pageX;
            dragStartPosition.y = e.changedTouches[0].pageY;
          }
        }}
        onDrag={() => {
          drag = true;
        }}
        onDragStop={(e, d) => {
          setPositionState({ x: d.x, y: d.y });

          if (isMobile && isAndroid) {
            const currentX = e.changedTouches[0].pageX;
            const currentY = e.changedTouches[0].pageY;
            if (
              currentX <= dragStartPosition.x + 3 &&
              currentX >= dragStartPosition.x - 3 &&
              currentY <= dragStartPosition.y + 3 &&
              currentY >= dragStartPosition.y - 3
            ) {
              drag = false;
            }
          }

          if (!drag) {
            handleClick();
          }
        }}
        enableResizing={false}
      >
        <div>
          {showSpeechBubble && (
            <SpeechBubble
              text={speechBubbleText}
              onClose={setShowSpeechBubble}
              textFontFamily={textFontFamily}
            />
          )}
          <Root
            id="btn"
            aria-label="chat launcher"
            position={positionState}
            visibilityButton={isOpened && !isMinimized}
            size={size}
            background={icon.background}
            roundedBorder={roundedBorder}
            disableBoxShadow={icon.disableBoxShadow}
            isAnimation={isAnimation}
            {...props}
          >
            <Avatar
              className="avatarIcon"
              url={img.url}
              urlHover={img.urlHover}
              size={size}
              isHover={isHover}
              isRealSize={icon.isRealSize || false}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            />
            {img.urlHover && <AvatarHover url={img.urlHover} />}
          </Root>
        </div>
      </Rnd>
    </Wrapper>
  );
}

VaIcon.propTypes = {
  isOpened: PropTypes.bool.isRequired,
  isMinimized: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  position: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired
  }).isRequired,
  size: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }).isRequired,
  textFontFamily: PropTypes.string,
  alternative: PropTypes.bool,
  img: PropTypes.shape({
    url: PropTypes.string.isRequired,
    urlHover: PropTypes.string,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired
  }).isRequired,
  icon: PropTypes.shape({
    background: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    disableBoxShadow: PropTypes.bool,
    isRealSize: PropTypes.bool
  }),
  isAnimation: PropTypes.bool,
  isHidden: PropTypes.bool,
  isSpeechBubble: PropTypes.bool,
  speechBubbleText: PropTypes.string,
  roundedBorder: PropTypes.bool
};

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 100000;
  width: 0;
  height: 0;
  background-color: transparent;
  box-sizing: border-box;

  * {
    box-sizing: border-box;
  }
`;

const bounce = keyframes`
  0% {
    transform: translateY(0);
  }
  20% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-30px);
  }
  50% {
    transform: translateY(0);
  }
  60% {
    transform: translateY(-15px);
  }
  80% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(0);
  }
`;

const Root = styled(Fab).attrs(({ size: { width, height }, background }) => ({
  style: {
    width,
    height,
    background
  }
}))`
  display: block;
  cursor: pointer;

  &:focus {
    outline: none !important;
  }

  ${({ disableBoxShadow }) =>
    disableBoxShadow &&
    css`
      box-shadow: none;
    `}

  ${({ isAnimation }) =>
    isAnimation &&
    css`
      animation: ${bounce} 2s ease 30;
    `}

  ${({ roundedBorder }) =>
    !roundedBorder &&
    css`
      border-radius: 0;
      box-shadow: none;
    `}
`;

const Avatar = styled.div.attrs(({ size: { width, height } }) => ({
  style: {
    width,
    height
  }
}))`
  display: block;
  background-image: ${({ url, urlHover, isHover }) =>
    isHover ? `url("${urlHover}")` : `url("${url}")`};
  background-repeat: no-repeat;
  background-position: 100% 100%;
  background-size: ${({ isRealSize }) => (isRealSize ? 'contain' : 'cover')};
`;

const AvatarHover = styled.div`
  background-image: ${({ url }) => `url("${url}")`};
`;
